import { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';

import { Avatar, Button, Textarea } from '../../../components/ui';
import { trpc } from '../../../lib/trpc';

export const Route = createFileRoute('/app/onboarding/step-1')({
  component: ProfileSetupStep,
});

function ProfileSetupStep() {
  const navigate = useNavigate();
  const { user: clerkUser } = useUser();
  const { data: me } = trpc.user.me.useQuery();

  const [bio, setBio] = useState('');

  const updateProfile = trpc.user.updateProfile.useMutation({
    onSuccess: () => {
      navigate({ to: '/app/onboarding/step-2' });
    },
    onError: () => {
      toast.error('Failed to update profile. Please try again.');
    },
  });

  const handleNext = () => {
    const trimmedBio = bio.trim();
    if (trimmedBio) {
      updateProfile.mutate({ bio: trimmedBio });
    } else {
      navigate({ to: '/app/onboarding/step-2' });
    }
  };

  const displayName = me?.username ?? clerkUser?.username ?? 'Player';
  const avatarUrl = clerkUser?.imageUrl ?? me?.avatarUrl ?? null;

  return (
    <div className="animate-fade-up p-4 md:p-6">
      {/* Card */}
      <div className="relative rounded-2xl bg-surface-900/80 border border-surface-700/50 overflow-hidden">
        {/* Top accent */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(38,217,181,0.04)_0%,_transparent_60%)] pointer-events-none" />

        <div className="relative px-6 sm:px-8 py-8 space-y-8">
          {/* Header */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">
              Set Up Your Profile
            </h1>
            <p className="text-sm text-surface-400">
              Let other players know who you are
            </p>
          </div>

          {/* Avatar + name display */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div className="absolute -inset-2 rounded-full bg-brand-500/10 blur-xl" />
              <Avatar
                src={avatarUrl}
                alt={displayName}
                initials={displayName.charAt(0)}
                size="xl"
                className="ring-2 ring-surface-700"
              />
            </div>
            <div className="text-center">
              <p className="font-display font-bold text-xl text-white">{displayName}</p>
              <p className="text-xs text-surface-500 mt-0.5">
                Display name synced from your login
              </p>
            </div>
          </div>

          {/* Bio input */}
          <Textarea
            label="Bio"
            placeholder="Tell us about yourself... (optional)"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={500}
            rows={3}
            resize="none"
          />
          <p className="text-xs text-surface-500 -mt-6 text-right">
            {bio.length}/500
          </p>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              onClick={() => navigate({ to: '/app/onboarding/step-2' })}
              disabled={updateProfile.isPending}
            >
              Skip
            </Button>
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={updateProfile.isPending}
              rightIcon={
                updateProfile.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <ArrowRight className="size-4" />
                )
              }
            >
              {updateProfile.isPending ? 'Saving...' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
